// Paddle Billing Provider Adapter
// Implements BillingProvider interface for Paddle.com using modern @paddle/paddle-js SDK

import {
  initializePaddle,
  Paddle,
  CheckoutSettings,
  EventName,
  CheckoutEventsData,
  PricePreviewResponse
} from '@paddle/paddle-js';
import {
  BaseBillingProvider,
  BillingProviderConfig,
  CheckoutOptions,
  CheckoutResult,
  CheckoutMode,
  PriceInfo
} from '../domain/billing-provider';
import { getPaddlePriceId } from '../config/paddle-prices';
import { TemplateLoader } from '../utils/template';

export class PaddleAdapter extends BaseBillingProvider {
  readonly name = 'paddle' as const;
  private paddleInstance: Paddle | null = null;
  private environment: 'sandbox' | 'production' = 'sandbox';
  private token: string = '';
  private popup: HTMLElement | null = null;
  private checkoutFrameTarget: string = 'paddle-checkout-container';
  private lastPricePreviewData: { country?: string; postalCode?: string } = {};

  async initialize(config: BillingProviderConfig): Promise<void> {
    if (this.initialized) {
      return;
    }

    this.environment = config.environment;
    this.token = config.clientToken || '';

    if (!this.token) {
      throw new Error('Paddle client token is required for initialization');
    }

    // Initialize Paddle SDK
    try {
      const paddleInstance = await initializePaddle({
        environment: this.environment,
        token: this.token,
        eventCallback: (event) => this.handlePaddleEvent(event)
      });

      if (!paddleInstance) {
        throw new Error('Failed to initialize Paddle instance');
      }

      this.paddleInstance = paddleInstance;
      this.initialized = true;
      console.log('[PaddleAdapter] Initialized in', this.environment, 'mode');
    } catch (error) {
      console.error('[PaddleAdapter] Initialization failed:', error);
      throw error;
    }
  }

  private handlePaddleEvent(event: { name: EventName; data?: CheckoutEventsData }): void {
    console.log('[PaddleAdapter] Event received:', event.name, event.data);

    switch (event.name) {
      case 'checkout.loaded':
        this.emit('checkout:opened', undefined);
        break;

      case 'checkout.completed':
        const completedData = event.data as any;
        const result: CheckoutResult = {
          status: 'success',
          transactionId: completedData?.transaction_id || completedData?.checkout?.id
        };
        this.emit('checkout:closed', result);
        this.emit('checkout:success', { transactionId: result.transactionId! });
        this.closePopupIfExists();
        break;

      case 'checkout.closed':
        // Only emit cancelled if not already completed
        const closedResult: CheckoutResult = { status: 'cancelled' };
        this.emit('checkout:closed', closedResult);
        this.emit('checkout:cancelled', undefined);
        this.closePopupIfExists();
        break;

      case 'checkout.error':
        const errorData = event.data as any;
        const errorResult: CheckoutResult = {
          status: 'error',
          error: errorData?.error || 'Unknown error occurred'
        };
        this.emit('checkout:closed', errorResult);
        this.emit('checkout:error', { error: errorResult.error! });
        this.closePopupIfExists();
        break;

      case 'checkout.customer.created':
      case 'checkout.customer.updated':
        // Handle customer events if needed
        console.log('[PaddleAdapter] Customer event:', event.name, event.data);
        break;

      case 'checkout.payment.selected':
      case 'checkout.payment.initiated':
      case 'checkout.payment.failed':
        // Handle payment events if needed
        console.log('[PaddleAdapter] Payment event:', event.name, event.data);
        break;
    }
  }

  async fetchPrices(priceIds: string[]): Promise<PriceInfo[]> {
    if (!this.initialized || !this.paddleInstance) {
      throw new Error('PaddleAdapter not initialized');
    }

    // Map product IDs to Paddle price IDs
    const paddlePriceIds = priceIds
      .map(id => getPaddlePriceId(id))
      .filter(id => id !== null) as string[];

    if (paddlePriceIds.length === 0) {
      console.warn('[PaddleAdapter] No valid Paddle price IDs found');
      return [];
    }

    try {
      console.log('[PaddleAdapter] Fetching prices for:', paddlePriceIds);

      // Use Paddle's PricePreview API
      const pricePreview: PricePreviewResponse = await this.paddleInstance.PricePreview({
        items: paddlePriceIds.map(priceId => ({
          priceId,
          quantity: 1
        }))
      });

      console.log('[PaddleAdapter] PricePreview response:', pricePreview);

      // Check if response has the expected structure
      if (!pricePreview?.data?.details?.lineItems) {
        console.error('[PaddleAdapter] Unexpected response structure:', pricePreview);
        throw new Error('Invalid PricePreview response structure');
      }

      // Store country and postal code from price preview if available
      if (pricePreview.data?.address) {
        this.lastPricePreviewData = {
          country: pricePreview.data.address.countryCode,
          postalCode: pricePreview.data.address.postalCode
        };
        console.log('[PaddleAdapter] Stored address data from price preview:', this.lastPricePreviewData);
      }

      // Get the actual currency from the response (not the price's base currency)
      const actualCurrency = pricePreview.data.currencyCode;

      const prices: PriceInfo[] = pricePreview.data.details.lineItems.map((item: any) => {
        // Parse amounts - they are in minor units (cents/pence)
        const subtotal = parseFloat(item.totals.subtotal) / 100;
        const tax = parseFloat(item.totals.tax) / 100;
        const total = parseFloat(item.totals.total) / 100;

        return {
          productId: item.price.id,
          amount: total, // Use total (inc. tax) as the main amount
          currency: actualCurrency, // Use actual currency from response
          currencySymbol: this.getCurrencySymbol(actualCurrency),
          tax: tax,
          total: total,
          formatted: {
            amount: item.formattedTotals?.total || this.formatCurrency(total, actualCurrency), // Use total for display
            tax: item.formattedTotals?.tax || this.formatCurrency(tax, actualCurrency),
            total: item.formattedTotals?.total || this.formatCurrency(total, actualCurrency)
          }
        };
      });

      console.log('[PaddleAdapter] Processed prices:', prices);
      this.emit('prices:updated', prices);
      return prices;
    } catch (error) {
      console.error('[PaddleAdapter] Price preview failed:', error);
      // Return empty array instead of throwing to allow graceful degradation
      return [];
    }
  }

  // Get last known address data from price preview
  getLastAddressData(): { country?: string; postalCode?: string } {
    return this.lastPricePreviewData;
  }

  async openCheckout(options: CheckoutOptions, mode: CheckoutMode): Promise<void> {
    console.log('[PaddleAdapter] openCheckout called with mode:', mode);

    if (!this.initialized || !this.paddleInstance) {
      throw new Error('PaddleAdapter not initialized');
    }

    // Use stored address data if not provided in options
    const country = options.country || this.lastPricePreviewData.country;
    const postalCode = options.postalCode || this.lastPricePreviewData.postalCode;

    console.log('[PaddleAdapter] Using address data:', { country, postalCode });

    // Build items array with both one-time and recurring prices if provided
    const items: Array<{ priceId: string; quantity: number }> = [];

    // Add recurring price (always required)
    if (!options.priceId) {
      throw new Error('Recurring price ID is required for checkout');
    }
    items.push({
      priceId: options.priceId,
      quantity: 1
    });

    // Add one-time price if provided (for initial trial payment)
    if (options.oneTimePriceId) {
      items.push({
        priceId: options.oneTimePriceId,
        quantity: 1
      });
    }

    console.log('[PaddleAdapter] Checkout items:', items);

    // Build checkout settings
    const checkoutSettings: CheckoutSettings = {
      displayMode: mode === 'inline' ? 'inline' : 'overlay',
      variant: 'one-page',  // Use one-page checkout variant
      theme: 'light',
      locale: 'en',
      allowLogout: false,

      // Add items to checkout
      items,

      // Customer information
      customer: options.email ? {
        email: options.email
      } : undefined,

      // Custom data
      customData: options.customData || {},

      // Success URL - current URL with #success
      successUrl: options.successUrl || `${window.location.origin}${window.location.pathname}#success`,

      // Frame settings for inline mode
      frameTarget: mode === 'inline' ? this.checkoutFrameTarget : undefined,
      frameInitialHeight: mode === 'inline' ? 450 : undefined,
      frameStyle: mode === 'inline' ? 'width: 100%; min-width: 100%; background-color: transparent; border: none;' : undefined
    };

    // Apply discount code if provided
    if (options.discountCode) {
      checkoutSettings.discountCode = options.discountCode;
    }

    // Apply address info if provided or available from price preview
    if (country || postalCode) {
      checkoutSettings.customer = {
        ...checkoutSettings.customer,
        address: {
          countryCode: country || '',
          postalCode: postalCode || ''
        }
      };
    }

    checkoutSettings.settings = {
      ...checkoutSettings.settings,
      displayMode: 'inline',
      variant: 'one-page',
      frameTarget: checkoutSettings.frameTarget,
      frameInitialHeight: checkoutSettings.frameInitialHeight,
      frameStyle: 'width: 100%; min-width: 312px; background-color: transparent; border: none;',
      successUrl: checkoutSettings.successUrl,
  };

    try {
      if (mode === 'inline') {
        console.log('[PaddleAdapter] Creating custom popup for inline checkout');
        // Create custom popup for inline checkout with pricing display info
        this.createCheckoutPopup(options.displayInfo);

        // Small delay to ensure DOM is ready
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      console.log('[PaddleAdapter] Opening Paddle checkout with settings:', checkoutSettings);

      // Open Paddle checkout
      this.paddleInstance.Checkout.open(checkoutSettings);
    } catch (error) {
      console.error('[PaddleAdapter] Failed to open checkout:', error);
      this.closePopupIfExists();
      throw error;
    }
  }

  private createCheckoutPopup(priceInfo?: { amount?: string; plan?: string }): void {
    // Load the checkout template
    const popupElement = TemplateLoader.loadTemplateElement('paddle-checkout-template') as HTMLElement;

    // Update the total amount if price info is provided
    if (priceInfo) {
      const totalAmountElement = popupElement.querySelector('.checkout-total-amount');
      if (totalAmountElement && priceInfo.amount && priceInfo.plan) {
        totalAmountElement.textContent = `${priceInfo.amount} for ${priceInfo.plan}`;
      }
    }

    // Set up close button
    const closeBtn = popupElement.querySelector('.checkout-close-btn');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.closeCheckout());
    }

    // Set up overlay click to close
    const overlay = popupElement.querySelector('.checkout-overlay');
    if (overlay) {
      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) {
          this.closeCheckout();
        }
      });
    }

    // Close on Escape key
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        this.closeCheckout();
        document.removeEventListener('keydown', handleEscape);
      }
    };
    document.addEventListener('keydown', handleEscape);

    // Append to body
    document.body.appendChild(popupElement);
    this.popup = popupElement;

    // Update the checkout frame target if needed
    const container = popupElement.querySelector('.checkout-container');
    if (container && container.id) {
      this.checkoutFrameTarget = container.id;
    }
  }

  private closePopupIfExists(): void {
    if (this.popup) {
      // Add fade out animation
      const overlay = this.popup.querySelector('.checkout-overlay') as HTMLElement;
      if (overlay) {
        overlay.style.animation = 'fadeOut 0.2s ease-out';
      }

      setTimeout(() => {
        if (this.popup) {
          this.popup.remove();
          this.popup = null;
        }
      }, 200);
    }
  }

  closeCheckout(): void {
    if (this.paddleInstance) {
      // Close Paddle checkout if open
      this.paddleInstance.Checkout.close();
    }
    this.closePopupIfExists();
  }

  async isApplePayAvailable(): Promise<boolean> {
    // Paddle.js supports Apple Pay through the checkout
    // Detection would need to be done at checkout time
    return false;
  }

  async isGooglePayAvailable(): Promise<boolean> {
    // Paddle.js supports Google Pay through the checkout
    // Detection would need to be done at checkout time
    return false;
  }

  private getCurrencySymbol(currency: string): string {
    const symbols: Record<string, string> = {
      USD: '$',
      EUR: '€',
      GBP: '£',
      CAD: 'C$',
      AUD: 'A$',
      JPY: '¥',
      CNY: '¥',
      INR: '₹',
      KRW: '₩',
      BRL: 'R$',
      MXN: '$',
      CHF: 'CHF',
      SEK: 'kr',
      NOK: 'kr',
      DKK: 'kr',
      PLN: 'zł',
      CZK: 'Kč',
      HUF: 'Ft',
      RON: 'lei',
      BGN: 'лв',
      HRK: 'kn',
      RUB: '₽',
      TRY: '₺',
      THB: '฿',
      IDR: 'Rp',
      MYR: 'RM',
      PHP: '₱',
      SGD: 'S$',
      VND: '₫',
      ZAR: 'R',
      AED: 'د.إ',
      SAR: 'ر.س',
      EGP: 'E£',
      NGN: '₦',
      KES: 'KSh'
    };
    return symbols[currency] || currency;
  }

  private formatCurrency(amount: number, currency: string): string {
    const symbol = this.getCurrencySymbol(currency);
    const formatter = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return `${symbol}${formatter.format(amount)}`;
  }

  destroy(): void {
    this.closeCheckout();
    this.paddleInstance = null;
    super.destroy();
  }
}